// client/src/pages/MyGifts.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api.js';
import { useAuth } from '../hooks/useAuth.js';

// ─── Helpers ──────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(cents) {
  if (!cents && cents !== 0) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function StatusBadge({ status }) {
  const styles = {
    pending_send: 'bg-amber-900/40 text-amber-400',
    sent: 'bg-blue-900/40 text-blue-400',
    redeemed: 'bg-green-900/40 text-green-400',
    expired: 'bg-royal-700 text-royal-100',
    abandoned: 'bg-red-900/40 text-red-400',
  };

  const labels = {
    pending_send: 'Incomplete',
    sent: 'Sent — Awaiting Redemption',
    redeemed: 'Redeemed',
    expired: 'Expired',
    abandoned: 'Abandoned',
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-sm ${styles[status] || 'bg-royal-700 text-royal-100'}`}>
      {labels[status] || status}
    </span>
  );
}

// ─── Dashboard Back Link ──────────────────────────────────
// Reusable so it can sit at the top of every state (loading, signed-out,
// main content) without repeating the markup.
function DashboardBackLink() {
  return (
    <div className="mb-5">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Dashboard
      </Link>
    </div>
  );
}

// ─── Sent Gift Card ──────────────────────────────────────

function SentGiftCard({ gift }) {
  const [showCode, setShowCode] = useState(false);

  const isExpiringSoon = gift.status === 'sent' && gift.giftcodeexpiresat &&
    (new Date(gift.giftcodeexpiresat) - new Date()) < 30 * 24 * 60 * 60 * 1000;

  return (
    <div className="bg-royal-900 dark:bg-royal-800/30 border border-royal-700 dark:border-royal-700/40 rounded-xl p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <h3 className="text-white font-medium text-base">{gift.planname}</h3>
            <StatusBadge status={gift.status} />
          </div>

          <div className="space-y-1 text-sm">
            {gift.recipientname && (
              <p className="text-royal-100">
                To: <span className="text-white">{gift.recipientname}</span>
                {gift.recipientemail && <span className="text-royal-100"> ({gift.recipientemail})</span>}
              </p>
            )}
            {!gift.recipientname && gift.recipientemail && (
              <p className="text-royal-100">To: <span className="text-white">{gift.recipientemail}</span></p>
            )}

            <p className="text-royal-100">
              Purchased: <span className="text-white">{formatDate(gift.createdat)}</span>
              <span className="text-royal-100 mx-1.5">·</span>
              {formatCurrency(gift.amount)}
            </p>

            {gift.status === 'sent' && gift.giftcodeexpiresat && (
              <p className={`text-sm ${isExpiringSoon ? 'text-amber-400' : 'text-royal-100'}`}>
                Redeemable until: <span className="text-white">{formatDate(gift.giftcodeexpiresat)}</span>
                {isExpiringSoon && ' — expiring soon'}
              </p>
            )}

            {gift.status === 'redeemed' && (
              <p className="text-green-400 text-sm">
                Redeemed on {formatDate(gift.redeemedat)}
                {gift.giftexpiresat && <span className="text-royal-100"> · Active until {formatDate(gift.giftexpiresat)}</span>}
              </p>
            )}

            {gift.sentat && (
              <p className="text-royal-100">Sent: {formatDate(gift.sentat)}</p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 flex-shrink-0">
          {gift.status === 'pending_send' && (
            <Link
              to={`/gift?resume=${gift.giftid}`}
              className="px-4 py-2 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors text-center"
            >
              Finish Sending
            </Link>
          )}

          {(gift.status === 'sent' || gift.status === 'pending_send') && (
            <button
              onClick={() => setShowCode(!showCode)}
              className="text-sm text-royal-100 hover:text-white transition-colors"
            >
              {showCode ? 'Hide Code' : 'Show Code'}
            </button>
          )}
        </div>
      </div>

      {showCode && gift.giftcode && (
        <div className="mt-3 pt-3 border-t border-royal-700/40">
          <div className="flex items-center gap-3 bg-royal-900/40 rounded-lg px-4 py-3">
            <div className="flex-1">
              <p className="text-royal-100 text-xs mb-0.5">Gift Code</p>
              <p className="text-gold font-mono font-bold tracking-wider select-all">{gift.giftcode}</p>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(gift.giftcode);
              }}
              className="px-3 py-1.5 border border-royal-600 text-royal-100 rounded-lg text-sm hover:text-white hover:border-royal-200 transition-colors"
            >
              Copy
            </button>
          </div>
          <p className="text-royal-100 text-xs mt-2">
            Redemption link: <span className="text-gold select-all break-all">{window.location.origin}/gift/redeem/{gift.giftcode}</span>
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Received Gift Card ──────────────────────────────────

function ReceivedGiftCard({ gift }) {
  return (
    <div className="bg-royal-900 dark:bg-royal-800/30 border border-royal-700 dark:border-royal-700/40 rounded-xl p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <h3 className="text-white font-medium text-base">{gift.planname}</h3>
            <StatusBadge status={gift.status} />
          </div>

          <div className="space-y-1 text-sm">
            {gift.sendername && (
              <p className="text-royal-100">From: <span className="text-white">{gift.sendername}</span></p>
            )}

            {gift.status === 'redeemed' && (
              <>
                <p className="text-green-400">Redeemed on {formatDate(gift.redeemedat)}</p>
                {gift.giftexpiresat && (
                  <p className="text-royal-100">
                    Active until: <span className="text-white">{formatDate(gift.giftexpiresat)}</span>
                  </p>
                )}
              </>
            )}

            <p className="text-royal-100">Received: {formatDate(gift.createdat)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────

export default function MyGifts() {
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('sent');
  const [sentGifts, setSentGifts] = useState([]);
  const [receivedGifts, setReceivedGifts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    setLoading(true);
    Promise.all([
      api.get('/gifts/my-gifts').catch(() => ({ gifts: [] })),
      api.get('/gifts/my-received').catch(() => ({ gifts: [] })),
    ])
      .then(([sentData, receivedData]) => {
        setSentGifts(sentData.gifts || []);
        setReceivedGifts(receivedData.gifts || []);
      })
      .finally(() => setLoading(false));
  }, [user]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="text-4xl mb-4">🎁</div>
          <h1 className="font-display text-2xl text-gold-dark dark:text-gold mb-3">My Gifts</h1>
          <p className="text-royal-600 dark:text-royal-100 text-sm mb-6">Sign in to view your gift history.</p>
          <Link to="/login?redirect=/gift/my-gifts" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors">
            Log In →
          </Link>
        </div>
      </div>
    );
  }

  const pendingGifts = sentGifts.filter((g) => g.status === 'pending_send');
  const completedSentGifts = sentGifts.filter((g) => g.status !== 'pending_send');
  const sentCount = sentGifts.length;
  const receivedCount = receivedGifts.length;

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* Dashboard Back Link */}
        <DashboardBackLink />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-2xl text-gold-dark dark:text-gold">My Gifts</h1>
            <p className="text-royal-600 dark:text-royal-100 text-sm mt-1">Track gifts you've sent and received</p>
          </div>
          <Link
            to="/gift"
            className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors text-center"
          >
            🎁 Gift a Plan
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('sent')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'sent'
                ? 'bg-gold text-royal-900 shadow-md'
                : 'bg-royal-900 dark:bg-royal-800/50 text-royal-100 border border-royal-700 dark:border-royal-600 hover:border-gold/50 hover:text-gold'
            }`}
          >
            Sent ({sentCount})
          </button>
          <button
            onClick={() => setActiveTab('received')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'received'
                ? 'bg-gold text-royal-900 shadow-md'
                : 'bg-royal-900 dark:bg-royal-800/50 text-royal-100 border border-royal-700 dark:border-royal-600 hover:border-gold/50 hover:text-gold'
            }`}
          >
            Received ({receivedCount})
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* ─── SENT TAB ──────────────────────────────── */}
            {activeTab === 'sent' && (
              <div className="space-y-6">
                {/* Pending / incomplete gifts */}
                {pendingGifts.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <h2 className="text-amber-400 font-semibold text-sm uppercase tracking-wider">Incomplete</h2>
                      <div className="flex-1 h-px bg-amber-600/30"></div>
                    </div>
                    <div className="space-y-3">
                      {pendingGifts.map((gift) => (
                        <SentGiftCard key={gift.giftid} gift={gift} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Completed sent gifts */}
                {completedSentGifts.length > 0 && (
                  <div>
                    {pendingGifts.length > 0 && (
                      <div className="flex items-center gap-3 mb-3">
                        <h2 className="text-royal-600 dark:text-royal-100 font-semibold text-sm uppercase tracking-wider">Sent</h2>
                        <div className="flex-1 h-px bg-royal-300/30 dark:bg-royal-700/50"></div>
                      </div>
                    )}
                    <div className="space-y-3">
                      {completedSentGifts.map((gift) => (
                        <SentGiftCard key={gift.giftid} gift={gift} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {sentGifts.length === 0 && (
                  <div className="text-center py-16">
                    <div className="text-4xl mb-4">🎁</div>
                    <p className="text-royal-900 dark:text-white text-base mb-2">You haven't sent any gifts yet</p>
                    <p className="text-royal-600 dark:text-royal-100 text-sm mb-6">Give someone the gift of sending beautiful eCards!</p>
                    <Link
                      to="/gift"
                      className="px-6 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors"
                    >
                      Gift a Plan
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* ─── RECEIVED TAB ──────────────────────────── */}
            {activeTab === 'received' && (
              <div>
                {receivedGifts.length > 0 ? (
                  <div className="space-y-3">
                    {receivedGifts.map((gift) => (
                      <ReceivedGiftCard key={gift.giftid} gift={gift} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="text-4xl mb-4">📬</div>
                    <p className="text-royal-900 dark:text-white text-base mb-2">No gifts received yet</p>
                    <p className="text-royal-600 dark:text-royal-100 text-sm">When someone gifts you a plan, it will appear here.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}